import { OneOfElements } from '@shared/models/project.model';
import {
  getElementQuantityRecord,
  isElementQuantityExpressionName,
} from '../../element-quantity.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';

type ElementQuantityExpressionChange =
  ProjectChangeMap[ProjectChangeType.ElementQuantityExpression];

/**
 * Applies a formula change to {@link IElementQuantityExpressionProperty.count}.
 * Bootstraps `count` from `fallbackCount` when none exists yet — this is the
 * path for the first time a user explicitly overrides an auto-quantity row.
 *
 * @param element - Current element state
 * @param change - Quantity expression payload
 * @returns Partial element update or undefined when not applicable / no-op
 */
export const elementQuantityExpressionToChange = (
  element: OneOfElements,
  change: ElementQuantityExpressionChange,
): Partial<OneOfElements> | undefined => {
  const quantityRecord = getElementQuantityRecord(element);
  const quantity = quantityRecord[change.quantityName];
  // Use name-based check to avoid getCount() throwing on legacy rows
  if (!quantity || !isElementQuantityExpressionName(quantity.name))
    return undefined;

  const existing = isExpressionValue(quantity.count)
    ? quantity.count
    : undefined;

  if (
    isExpressionValue(existing) &&
    existing.expression === change.expression
  ) {
    return undefined;
  }

  // Only spread object-shaped expression values; quantity row typing is still a union here
  const baseCount =
    (isExpressionValue(quantity.fallbackCount)
      ? quantity.fallbackCount
      : undefined) ??
    existing ??
    {};
  const newCount = {
    ...baseCount,
    expression: change.expression,
  };
  return {
    quantity: {
      ...quantityRecord,
      [change.quantityName]: {
        ...quantity,
        count: newCount,
      },
    },
  };
};
